import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  type FieldPath,
  type FieldValues,
  type UseControllerProps,
  useController,
} from "react-hook-form";

export default function Select<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  placeholder,
  required,
  disabled,
  ...rest
}: {
  options: string[] | readonly string[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
} & UseControllerProps<TFieldValues, TName>) {
  const { field } = useController(rest);
  const formattedPlaceholder = required ? `${placeholder}*` : placeholder;
  const value =
    field.value ?? formattedPlaceholder ?? "None";

  return (
    <div className="relative w-full">
      <Listbox {...field} value={value}>
        <div className="relative w-full">
          <Listbox.Button
            role="combobox"
            aria-required
            className="h-15 w-full cursor-default rounded-sm border border-white bg-black px-7 py-3 text-left text-lg font-normal leading-relaxed text-white placeholder-white shadow-md focus-visible:bg-blue focus-visible:outline-none"
          >
            <span className="block truncate">
              {value === "None" ? formattedPlaceholder : value}
            </span>
            <span className=" pointer-events-none absolute inset-y-0 right-0 flex items-center pr-7 text-white">
              <span className="material-icons-round">arrow_drop_down</span>
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-1 max-h-60 w-full overflow-auto rounded-sm bg-white text-base shadow-lg focus:outline-none sm:text-sm">
              {placeholder && (
                <Listbox.Option
                  className="relative cursor-default select-none rounded-sm px-7 py-3 text-lg leading-relaxed"
                  value="None"
                  hidden
                  disabled
                >
                  <span className="block truncate font-normal">
                    {placeholder}
                  </span>
                </Listbox.Option>
              )}
              {options.map((option) => (
                <Listbox.Option
                  key={option}
                  className={({ active }) =>
                    `relative cursor-default select-none rounded-sm px-7 py-3 text-lg leading-relaxed ${
                      active ? "cursor-pointer bg-blue text-white" : ""
                    }`
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {option}
                      </span>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {required && (
        <div
          className="isolate select-none opacity-0"
          aria-hidden="true"
          tabIndex={-1}
          style={Object.assign(
            {},
            { cursor: `${disabled ? "not-allowed" : "pointer"}` },
          )}
        >
          <div
            className="absolute bottom-[15px] left-0 z-10 h-[1px] w-full opacity-0"
            aria-hidden="true"
            tabIndex={-1}
          ></div>
          <select
            disabled={disabled}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClick={() => {}}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={() => {}}
            tabIndex={-1}
            value={value ?? undefined}
            name={field.name}
            required={required}
            aria-hidden="true"
            autoCapitalize="off"
            autoComplete="off"
            className="absolute bottom-[15px] left-0 z-0 h-[1px] w-full select-none appearance-none bg-transparent text-transparent opacity-0 shadow-none !outline-none"
          >
            <option value="" defaultChecked aria-hidden="true"></option>
            {options.map((e) => {
              return (
                <option key={e} value={e} aria-hidden="true">
                  {e}
                </option>
              );
            })}
          </select>
        </div>
      )}
    </div>
  );
}
